import React, { useEffect, useState } from "react";
import Spinner from "../../common/Spinner/Spinner";
import { useLocation, useNavigate } from "react-router-dom";
import { AssetDetailsColumns, AssetDetailsColumnsHeaders, AssetDetailsColumnsHeadersForHigherVersion } from "../../SiteSummary/constants";
import { AssetTable } from "../AssetTable";
import LoaderOverlay from "../../common/Loader/LoaderOverlay";
import RunsChart from "../../common/Charts/RunsChart";
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';


// Define a type for the field state
interface AssetRunsProps {
    assetId: string,
    customerId: string,
    userTimeZone: string,
    handleLogout: () => void,
    wellVersion?: string,
    assetName?: string
}


// Define a type for the field state

const AssetRunsForm: React.FC<AssetRunsProps> = ({ assetName, assetId, customerId, userTimeZone, handleLogout, wellVersion }) => {

    const [page, setPage] = useState<number>(1);
    const [wellRunDetails, setWellRunData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAnalysisButtonDisabled, setIsAnalysisButtonDisabled] = useState(false)
    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;
    const queryParams = {
        // Add your query parameters here
        wellId: assetId,
        customerId: customerId,
        timeZoneId: userTimeZone
    };
    const [isExpandedChart, setisExpandedChart] = useState(true);
    const [isExpandedChartTable, setisExpandedChartTable] = useState(true);

    const toggleExpand = () => {
        setisExpandedChart(!isExpandedChart);
    };

    const toggleExpandChart = () => {
        setisExpandedChartTable(!isExpandedChartTable);
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const apiUrl = `${baseUrl}/Wells/GetALLRunsData?${queryString}`;

    const handlePageNumberChange = (pageNumber: number) => setPage(pageNumber);

    useEffect(() => {
        const fetchData = async () => {
            const headers = {
                'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
                'Content-Type': 'application/json' // Adjust content type if needed
            }
            try {
                const response = await fetch(apiUrl, { headers });
                if (response.status === 401) {
                    handleLogout()
                }
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                setWellRunData(data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false); // Set loading to false whether the fetch was successful or not
            }
        };

        fetchData();

    }, [assetId]);

    // Function to capitalize the first letter of each key
    const capitalizeAndRenameKeys = (data) => {
        const keyMappings = {
            Run: "Run",
            Date: "Date",
            CloseDuration: "Close Duration",
            FallDuration: "Fall Duration",
            FallVelocity: "Fall Velocity",
            OpenReason: "Open Reason",
            CasingOpen: "Casing Open",
            TubingOpen: "Tubing Open",
            LineOpen: "Line Open",
            LoadFactor: "Load Factor",
            OpenSlugVolume: "Open Slug Volume",
            ArrivalDuration: "Arrival Duration",
            ArrivalVelocity: "Arrival Velocity",
            CasingArrival: "Casing Arrival",
            TubingArrival: "Tubing Arrival",
            LineArrival: "Line Arrival",
            SalesDuration: "Sales Duration",
            CloseReason: "Close Reason",
            CasingClose: "Casing Close",
            TubingClose: "Tubing Close",
            LineClose: "Gas Rate at Close",
            SlugDuration: "Slug Duration",
            SlugVolume: "Slug Volume",
            LiftEfficency: "Lift Efficiency %",
            GasVolume: "Gas Volume This Cycle",
            GasVolumeForecast: "Gas Volume Daily Projected"
        };
    
        return data.map((item) => {
            const transformedItem = {};
    
            Object.keys(item).forEach((key) => {
                const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
                if (capitalizedKey === "FastArrival") return;
    
                let value = item[key];
    
                // Format Date as text so Excel doesn't misinterpret
                if (capitalizedKey === "Date" && value) {
                    const date = new Date(value);
                    const formattedDate =
                        `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
                        `${date.getDate().toString().padStart(2, '0')}/` +
                        `${date.getFullYear().toString().slice(-2)} ` +
                        `${date.getHours().toString().padStart(2, '0')}:` +
                        `${date.getMinutes().toString().padStart(2, '0')}:` +
                        `${date.getSeconds().toString().padStart(2, '0')}`;
                        value = `\t${formattedDate}`; // Excel will treat as text to preserve format
                }
    
                // Keep numbers as-is — do NOT add \t or quotes
                transformedItem[keyMappings[capitalizedKey] || capitalizedKey] = value;
            });
    
            return transformedItem;
        });
    };
    

    const downloadCSV = (data, filename = 'data.csv') => {
        // Transform data to capitalize keys and fix formats
        const transformedData = capitalizeAndRenameKeys(data);

        // Convert data to CSV format with Excel-safe options
        const csv = Papa.unparse(transformedData, {
            quotes: false,  // Wrap all fields in quotes to prevent Excel formatting
            quoteChar: '"',
            delimiter: ",",
            escapeChar: '"'
        });


        // Create a UTF-8 BOM for Excel to read correctly
        const BOM = "\uFEFF";  // Ensures special characters (UTF-8) display correctly in Excel
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

        // Use FileSaver.js to save the Blob as a file
        saveAs(blob, filename);
    };


    const handleExport = (runsData, fileName) => {
        setIsAnalysisButtonDisabled(true)
        downloadCSV(runsData, fileName);
        setIsAnalysisButtonDisabled(false);
    };


    return (
        <div className="bg-darkcolor-800 content-wrapper">
            {/* {loading && <LoaderOverlay />} */}
            <div
                onClick={toggleExpand}
                style={{ backgroundColor: "#4A5463", marginLeft: "15px", marginRight: "15px", marginBottom: "16px" }}
                className="mx-auto pl-[15px] pr-[15px] flex items-center cursor-pointer p-2"
            >
                <div
                    style={{
                        backgroundColor: "var(--Primary-Indigo-400, #60BFDA)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "22px",
                        height: "20px",
                        borderRadius: "10%"
                    }}
                    className="p-2"
                >
                    <FontAwesomeIcon icon={isExpandedChart ? faMinus : faPlus} />
                </div>
                <span
                    style={{ color: "var(--Primary-Indigo-400, #60BFDA)", marginLeft: "5px" }}
                >
                    Run Charts
                </span>
            </div>

            {isExpandedChart &&
                <div className="border border-darkcolor-500 ml-[15px] mr-[15px] mt-[-17px] mb-[15px] rounded-lg" style={{ borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                    <div className="flex bg-darkcolor-650 text-white">
                        <div className="w-full mx-auto">
                            <div className="ml-[15px] mr-[15px] border border-darkcolor-500 mb-[15px] mt-[15px]" style={{ backgroundColor: "rgb(37 48 64 / var(--tw-bg-opacity))", height: "400px", paddingRight: "30px", paddingTop: "20px", paddingBottom: "20px" }}>
                                <RunsChart trendData={wellRunDetails} />
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div
                onClick={toggleExpandChart}
                style={{ backgroundColor: "#4A5463", marginLeft: "15px", marginRight: "15px" }}
                className="mx-auto pl-[15px] pr-[15px] flex items-center cursor-pointer p-2"
            >
                <div
                    style={{
                        backgroundColor: "var(--Primary-Indigo-400, #60BFDA)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "22px",
                        height: "20px",
                        borderRadius: "10%"
                    }}
                    className="p-2"
                >
                    <FontAwesomeIcon icon={isExpandedChartTable ? faMinus : faPlus} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span
                        style={{ color: "var(--Primary-Indigo-400, #60BFDA)", marginLeft: "5px" }}
                    >
                        Run
                    </span>
                    <button
                        type="button"
                        disabled={isAnalysisButtonDisabled}
                        className={`btn btn-primary ${isAnalysisButtonDisabled ? 'disabled-button' : ''}`}
                        style={{ backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                        onClick={(e) => {
                            handleExport(wellRunDetails, `${assetName}_Run.csv`)
                            e.stopPropagation()
                        }}
                    >
                        Export To CSV
                    </button>
                </div>


            </div>
            {isExpandedChartTable && <div className="border border-darkcolor-500 ml-[15px] mr-[15px] pb-[15px] rounded-lg" style={{ borderTop: 'none', borderRadius: '0 0 8px 8px' }}>

                <div className="pl-[15px] pr-[15px] mx-auto flex mt-[-10px]">
                    <AssetTable data={wellRunDetails} columns={AssetDetailsColumns} defaultSortColumn="run" defaultSortOrder="asc" pageSize={100} columnHeaders={AssetDetailsColumnsHeadersForHigherVersion} pagniation={true} page={page} handlePageNumberChange={handlePageNumberChange} assetId = {assetId} />
                </div>
            </div>
            }

            <div className="mb-[15px]" />
        </div>
    );
};

export default AssetRunsForm;
